'use client';

import { BodyStrong, MetaDefault } from '../../../components/typography';
import { useMemo, useState } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Badge } from '../../../components/badge';
import { Button } from '../../../components/button';
import { IconButton } from '../../../components/icon-button';
import { Menu } from '../../../components/menu';
import { Switch } from '../../../components/switch';
import { Tabs } from '../../../components/tabs';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  MoreVerticalIcon,
  Tv01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useDateFormatter } from '@terra/ui-formatters';
import { isCredentialVerificationInProgress } from '@terra/shared-utils';
import type { CredentialsApiDto } from '@terra/shared-contracts-applications';
import {
  BasicInformationTab,
  LoginInformationTab,
  RequestAuthenticationTab,
} from './CredentialTabs';
import { VerificationBadge } from './VerificationBadge';
import { CredentialLoginHealthBadge } from '../../application-credentials/CredentialLoginHealthBadge';
import { FeatureAnalysisCredentialBadge } from '../../application-credentials/FeatureAnalysisCredentialBadge';
import {
  CredentialDataSourcesProvider,
  type CredentialDataSources,
} from '../CredentialDataSourcesContext';

type Credential = CredentialsApiDto['credentials'][number];

export type DisableCredentialParams = {
  credentialId: string;
  isDisabled: boolean;
};
export type CredentialCardProps = {
  applicationId: string;
  credential: Credential;
  onEdit: (credential: Credential) => void;
  onDelete?: (credential: Credential) => void;
  onTest: (credentialId: string) => void;
  onAbort?: (credentialId: string) => void;
  onDisable?: (params: DisableCredentialParams) => void;
  isTesting?: boolean;
  isTestingAllCredentials?: boolean;
  isReadOnly?: boolean;
  verificationStreamingId?: string;
  onOpenVerificationStream?: (credentialId: string) => void;
  dataSources?: CredentialDataSources;
  loginBehaviorHref?: string;
};
const NO_DATA_SOURCES: CredentialDataSources = {};
const CredentialVerificationInfo = ({
  credential,
  isTesting,
}: Pick<CredentialCardProps, 'credential' | 'isTesting'>) => {
  const t = useTranslations('designSystem.credentials.card');
  const dateFormatter = useDateFormatter();
  const statusText = useMemo(() => {
    if (isTesting) {
      return null;
    }

    if (
      credential.verification?.status === 'VERIFIED' &&
      credential.verification?.verifiedAt
    ) {
      return `${t('verifiedAt')} ${dateFormatter.format(new Date(credential.verification.verifiedAt))}`;
    }

    if (credential.verification?.status === 'FAILED') {
      return t('failedVerify');
    }

    return null;
  }, [
    isTesting,
    credential.verification?.status,
    credential.verification?.verifiedAt,
    dateFormatter,
    t,
  ]);

  if (statusText) {
    return (
      <HStack gap={1} alignItems="center">
        <MetaDefault color="fg.neutral.muted">{statusText}</MetaDefault>
      </HStack>
    );
  }

  return null;
};

export const CredentialCard = ({
  applicationId,
  credential,
  onEdit,
  onDelete,
  onTest,
  onAbort,
  onDisable,
  isTesting,
  isTestingAllCredentials,
  isReadOnly,
  verificationStreamingId,
  onOpenVerificationStream,
  dataSources = NO_DATA_SOURCES,
  loginBehaviorHref,
}: CredentialCardProps) => {
  const t = useTranslations('designSystem.credentials.card');
  const [isExpanded, setIsExpanded] = useState(false);
  const sessionArtifacts = credential.verification?.artifacts ?? [];
  const isVerificationInProgress = isCredentialVerificationInProgress({
    status: credential.verification?.status,
  });
  const isVerificationActive = Boolean(isTesting) || isVerificationInProgress;
  const showVerificationAbort = Boolean(onAbort) && Boolean(isTesting);
  const showVerificationStream =
    Boolean(verificationStreamingId) &&
    Boolean(onOpenVerificationStream) &&
    isVerificationActive;
  const menuItems = [
    {
      value: 'edit',
      label: t('menu.edit'),
      disabled: isReadOnly,
      onClick: () => {
        if (!isReadOnly) {
          onEdit(credential);
        }
      },
    },
    {
      value: 'test',
      label: t('menu.test'),
      disabled: isTesting || isTestingAllCredentials,
      onClick: () => {
        onTest(credential.id);
      },
    },
    ...(onDelete
      ? [
          {
            value: 'delete',
            label: t('menu.delete'),
            disabled: isReadOnly,
            onClick: () => {
              if (!isReadOnly) {
                onDelete(credential);
              }
            },
          },
        ]
      : []),
  ];

  return (
    <CredentialDataSourcesProvider dataSources={dataSources}>
      <Box
        borderWidth="1px"
        borderColor="border.neutral.subtle"
        borderRadius="md"
        bg="bg.neutral.default"
        shadow={credential.isDisabled ? 'none' : 'xs'}
        overflow="hidden"
      >
        <HStack
          gap={2}
          px={4}
          py={3}
          alignItems="center"
          cursor="pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <IconButton
            variant="ghost"
            size="xs"
            aria-label={isExpanded ? t('aria.collapse') : t('aria.expand')}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? (
              <ArrowDown01Icon size={16} />
            ) : (
              <ArrowRight01Icon size={16} />
            )}
          </IconButton>

          <VStack alignItems="flex-start" gap={1.5} flex={1} minW={0}>
            <HStack gap={1} alignItems="center" w="full" flexWrap="wrap">
              <BodyStrong color="fg.neutral.default" truncate>
                {credential.name || credential.username || t('noUsername')}
              </BodyStrong>
              <Badge variant="outline" size="sm">
                {credential.role || t('defaultRole')}
              </Badge>
              <FeatureAnalysisCredentialBadge
                generatedBy={credential.generatedBy}
              />
              <VerificationBadge
                credential={credential}
                isTesting={isTesting}
                artifacts={sessionArtifacts}
              />
              <CredentialLoginHealthBadge
                loginHealth={credential.loginHealth}
                loginBehaviorHref={loginBehaviorHref}
              />
            </HStack>
            <CredentialVerificationInfo
              credential={credential}
              isTesting={isTesting}
            />
          </VStack>

          <HStack gap={2} onClick={(e) => e.stopPropagation()}>
            {showVerificationStream ? (
              <IconButton
                aria-label={t('aria.viewVerificationStream')}
                variant="subtle"
                size="xs"
                onClick={() => onOpenVerificationStream?.(credential.id)}
              >
                <Tv01Icon size={14} />
              </IconButton>
            ) : null}
            {showVerificationAbort ? (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => onAbort?.(credential.id)}
              >
                {t('menu.abort')}
              </Button>
            ) : null}
            {onDisable && (
              <Switch
                size="sm"
                checked={!credential.isDisabled}
                disabled={isReadOnly}
                onCheckedChange={({ checked }) => {
                  if (!isReadOnly) {
                    onDisable({
                      credentialId: credential.id,
                      isDisabled: !checked,
                    });
                  }
                }}
              />
            )}
            <Menu.MenuWithItems
              items={menuItems}
              trigger={
                <IconButton
                  variant="outline"
                  size="xs"
                  aria-label={t('menu.label')}
                >
                  <MoreVerticalIcon size={16} />
                </IconButton>
              }
            />
          </HStack>
        </HStack>

        {isExpanded && (
          <Box px={4} pb={4}>
            <Tabs.Root defaultValue="basicInfo" size="sm">
              <Tabs.List>
                <Tabs.Trigger value="basicInfo">
                  {t('tabs.basicInfo.title')}
                </Tabs.Trigger>
                <Tabs.Trigger value="loginInfo">
                  {t('tabs.loginInfo.title')}
                </Tabs.Trigger>
                <Tabs.Trigger value="requestAuth">
                  {t('tabs.requestAuth.title')}
                </Tabs.Trigger>
              </Tabs.List>
              <Tabs.Content value="basicInfo">
                <BasicInformationTab
                  applicationId={applicationId}
                  credential={credential}
                />
              </Tabs.Content>
              <Tabs.Content value="loginInfo">
                <LoginInformationTab credential={credential} />
              </Tabs.Content>
              <Tabs.Content value="requestAuth">
                <RequestAuthenticationTab credential={credential} />
              </Tabs.Content>
            </Tabs.Root>
          </Box>
        )}
      </Box>
    </CredentialDataSourcesProvider>
  );
};
